/**
 * <StatCard> — 13-ui-screens.md §6.2, §10.2; 14-theme.md §7.8.
 *
 * The value drops from 24 px to 18 px once the rendered string exceeds 8
 * characters, so `RM 418,204.60` stays on one line.
 */
import type { ReactNode } from 'react';

export type StatTone = 'plain' | 'green' | 'amber' | 'red';

export interface StatCardProps {
  label: string;
  /** Pass an ALREADY-FORMATTED string. Never a number, never a raw wire value. */
  value: string;
  note?: ReactNode;
  tone?: StatTone;
  onClick?: () => void;
  /** `--cm-figure-stat-lg` (30 px) — the document-expiry counts. */
  large?: boolean;
}

export function StatCard({
  label,
  value,
  note,
  tone = 'plain',
  onClick,
  large = false,
}: StatCardProps) {
  const cls = ['cm-stat', tone !== 'plain' && `cm-stat--${tone}`, onClick && 'cm-liftable']
    .filter(Boolean)
    .join(' ');

  const valueCls = ['cm-stat__value', !large && value.length > 8 && 'cm-stat__value--long']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cls}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      <div className="cm-stat__label">{label}</div>
      <div
        className={valueCls}
        style={large ? { fontSize: 'var(--cm-figure-stat-lg)', lineHeight: 1.2 } : undefined}
      >
        {value}
      </div>
      {note ? <div className="cm-stat__note">{note}</div> : null}
    </div>
  );
}
